import React, { useState, useEffect } from 'react';
import { formatDistanceToNow } from 'date-fns';
import { History, RotateCcw, Eye, Loader2 } from 'lucide-react';
import { Button } from './ui/Button';
import { supabase } from '../lib/supabase';
import { useTranslation } from '../lib/i18n';

interface Version {
  id: string;
  title: string;
  content: { text: string } | null;
  created_at: string;
  created_by: string;
}

interface VersionHistoryProps {
  documentId: string;
  currentTitle: string;
  onRestoreVersion: (version: Version) => void;
  onViewVersion: (version: Version) => void;
}

const VersionHistory: React.FC<VersionHistoryProps> = ({
  documentId,
  currentTitle,
  onRestoreVersion,
  onViewVersion
}) => {
  const { t } = useTranslation();
  const [versions, setVersions] = useState<Version[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedVersion, setSelectedVersion] = useState<string | null>(null);

  useEffect(() => {
    loadVersions();
  }, [documentId]);

  const loadVersions = async () => {
    try {
      setLoading(true);
      setError(null);

      const { data, error: fetchError } = await supabase
        .from('document_versions')
        .select(`
          id,
          title,
          content,
          created_at,
          created_by,
          users (
            email,
            full_name
          )
        `)
        .eq('document_id', documentId)
        .order('created_at', { ascending: false });

      if (fetchError) throw fetchError;

      setVersions(data || []);
    } catch (err: any) {
      console.error('Error loading versions:', err);
      setError(err.message || t('version.failedLoad'));
    } finally {
      setLoading(false);
    }
  };

  const handleViewClick = (version: Version) => {
    if (version.content?.text) {
      onViewVersion(version);
    }
  };

  const handleRestoreClick = (version: Version) => {
    if (version.content?.text) {
      setSelectedVersion(version.id);
      onRestoreVersion(version);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin mx-auto mb-4 text-blue-500" />
          <p className="text-gray-500 dark:text-gray-400">{t('version.loading')}</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4 bg-red-50 dark:bg-red-900/50 text-red-700 dark:text-red-200 rounded-lg">
        <p className="text-sm">{error}</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold">{t('version.title')}</h3>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            {versions.length === 1 ? t('version.count', { count: versions.length }) : t('version.countPlural', { count: versions.length })}
          </p>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={() => loadVersions()}
          className="text-blue-600 dark:text-blue-400"
        >
          <History className="w-4 h-4 mr-2" />
          {t('version.refresh')}
        </Button>
      </div>

      {versions.length === 0 ? (
        <div className="text-center py-8">
          <History className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <h4 className="text-lg font-medium mb-2">{t('version.noHistory')}</h4>
          <p className="text-gray-500 dark:text-gray-400">
            {t('version.noHistoryDesc')}
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {/* Current Version */}
          <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg">
            <div className="flex items-center justify-between">
              <div>
                <h4 className="font-medium">{t('version.current')}</h4>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  {currentTitle}
                </p>
              </div>
            </div>
          </div>

          {/* Version List */}
          <div className="space-y-2">
            {versions.map((version) => (
              <div
                key={version.id}
                className={`p-4 rounded-lg border ${
                  selectedVersion === version.id
                    ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                    : 'border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800'
                }`}
              >
                <div className="flex items-center justify-between">
                  <div>
                    <h4 className="font-medium">{version.title}</h4>
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      {formatDistanceToNow(new Date(version.created_at), { addSuffix: true })}
                    </p>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleViewClick(version)}
                      className="text-gray-600 dark:text-gray-300"
                      disabled={!version.content?.text}
                    >
                      <Eye className="w-4 h-4 mr-2" />
                      {t('version.view')}
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleRestoreClick(version)}
                      className="text-blue-600 dark:text-blue-400"
                      disabled={!version.content?.text}
                    >
                      <RotateCcw className="w-4 h-4 mr-2" />
                      {t('version.restore')}
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default VersionHistory;